import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getYourData } from '../../utils/yourDataStore';
import { Box, Stack, Typography, Button, TextField, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../Layouts/DashboardLayout';
import DropdownArrowIcon from '../icons/DropdownArrowIcon';
import ReviewsScoring from '../Performance/components/ReviewsScoring';
import GoogleAdsKeywordService from '../../services/GoogleAdsKeywordService';

import logger from '../../utils/logger';
const MainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '40px',
  alignItems: 'flex-start',
  maxWidth: '1200px'
}));

const ContentSection = styled(Stack)(({ theme }) => ({
  flex: 1,
  gap: '40px'
}));

const HeaderSection = styled(Stack)(({ theme }) => ({
  gap: '6px'
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '28px',
  fontWeight: 600,
  color: '#0B91D6'
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 500,
  color: '#121927'
}));

const Description = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#30302E',
  lineHeight: '20px'
}));

const FormSection = styled(Stack)(({ theme }) => ({
  gap: '40px'
}));

const FormContainer = styled(Stack)(({ theme }) => ({
  gap: '24px'
}));

const FormFieldContainer = styled(Stack)(({ theme }) => ({
  gap: '24px'
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  fontWeight: 400,
  color: '#121927',
  marginBottom: '8px'
}));

const StyledSelect = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 18px',
  borderRadius: '8px',
  border: '0.2px solid #A0A0AA',
  backgroundColor: '#ffffff',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#0B91D6'
  }
}));

const SelectPlaceholder = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#A0A0AA'
}));

const KeywordsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '14px',
  marginTop: '16px'
}));

const KeywordChip = styled(Chip)(({ theme, selected }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  height: '32px',
  borderRadius: '16px',
  backgroundColor: selected ? '#34A853' : '#ffffff',
  color: selected ? '#ffffff' : '#121927',
  border: selected ? 'none' : '1px solid #E5E7EB',
  '&:hover': {
    backgroundColor: selected ? '#2E8B47' : '#F9FAFB'
  },
  '& .MuiChip-label': {
    padding: '6px 12px'
  }
}));

const ManualKeywordsField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    '& fieldset': {
      border: '0.2px solid #A0A0AA'
    },
    '&:hover fieldset': {
      borderColor: '#0B91D6'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0B91D6',
      borderWidth: '1px'
    },
    '& input': {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      fontWeight: 400,
      color: '#121927',
      padding: '16px 18px',
      '&::placeholder': {
        color: '#A0A0AA',
        opacity: 1
      }
    }
  }
}));

const ContinueButton = styled(Button)(({ theme }) => ({
  alignSelf: 'center',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'capitalize',
  width: '140px',
  height: '40px',
  padding: 0,
  borderRadius: '8px',
  backgroundColor: '#0B91D6',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#0980C2'
  }
}));

// Dynamic keywords will be generated based on business category
const defaultKeywords = [
  'Business services',
  'Local business', 
  'Professional services',
  'Customer service',
  'Quality service',
  'Best service'
];

const ProfileStrengthAnalysis = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedBusinesses = location.state?.businesses || [];
  const accounts = passedBusinesses.map(b => {
    // Try to derive accountId and locationId from multiple sources
    const parsedAccId = (typeof b?.name === 'string' && b.name.includes('accounts/')) ? b.name.split('/')[1] : undefined;
    const accId = b.accountId || b.accountName || parsedAccId;
    const parsedLocId = (typeof b?.name === 'string' && b.name.includes('/locations/')) ? b.name.split('/').pop() : undefined;
    const locId = b.locationId || b.id || parsedLocId;
    return {
      id: accId || locId, // keep backward compatibility but prefer account id
      name: b.name || b.businessName,
      address: b.address || b.formattedAddress,
      raw: b,
      accountId: accId,
      locationId: locId
    };
  });

  const [selectedAccount, setSelectedAccount] = useState(location.state?.selected?.name || '');
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [manualKeywords, setManualKeywords] = useState('');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [competitorScoreData, setCompetitorScoreData] = useState(null);
  const [dynamicKeywords, setDynamicKeywords] = useState(defaultKeywords);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [businessData, setBusinessData] = useState(null);
  const keywordsFetchedRef = useRef(false);

  const accountSelectRef = useRef(null);
  const keywordsSelectRef = useRef(null);
  const manualInputRef = useRef(null);

  // Load business data from localStorage first (has full GMB data with categories) - same as CompetitorAnalysisModal
  useEffect(() => {
    // Reset fetch flag when account changes
    keywordsFetchedRef.current = false;
    
    if (!selectedAccount) {
      setBusinessData(null);
      setDynamicKeywords(defaultKeywords);
      return;
    }

    // Priority 1: Try to load from localStorage selectedBusiness (has full GMB data with categories)
    try {
      const selectedBusiness = localStorage.getItem('selectedBusiness');
      if (selectedBusiness) {
        const business = JSON.parse(selectedBusiness);
        // Check if this business matches the selected account or has category info
        if (business.categories || business.name === selectedAccount || business.title === selectedAccount) {
          setBusinessData(business);
          logger.log('[ProfileStrengthAnalysis] Set business data from localStorage (with categories):', business.name || business.title);
          logger.log('[ProfileStrengthAnalysis] Categories:', business.categories);
          return;
        }
      }
    } catch (e) {
      logger.error('[ProfileStrengthAnalysis] Error loading from localStorage:', e);
    }

    // Priority 2: Fall back to selected business from accounts
    const selectedBiz = accounts.find(a => a.name === selectedAccount);
    const rawBusinessData = selectedBiz?.raw;
    
    if (rawBusinessData) {
      setBusinessData(rawBusinessData);
      logger.log('[ProfileStrengthAnalysis] Set business data from accounts:', rawBusinessData.name || rawBusinessData.title);
    }
  }, [selectedAccount]);

  // Fetch dynamic keywords when businessData is available (same as HeatmapPage/CompetitorAnalysisModal)
  useEffect(() => {
    const fetchDynamicKeywords = async () => {
      // Prevent duplicate fetches
      if (keywordsFetchedRef.current || !businessData) {
        return;
      }
      
      keywordsFetchedRef.current = true;

      try {
        setKeywordsLoading(true);
        logger.log('[ProfileStrengthAnalysis] Fetching keywords for business:', businessData.name || businessData.title);
        
        // Extract business category from GMB data (same as CompetitorAnalysisModal)
        const businessCategory = GoogleAdsKeywordService.extractBusinessCategory(businessData);
        
        logger.log('[ProfileStrengthAnalysis] Business category:', businessCategory);
        
        // Get real keywords from DataForSEO API (India only) - same as CompetitorAnalysisModal
        const keywords = await GoogleAdsKeywordService.getKeywordSuggestions({
          businessCategory,
          location: 'India'
        });
        
        // Extract search terms for selection
        const keywordTerms = keywords.slice(0, 6).map(k => k.searchTerm);
        
        logger.log('[ProfileStrengthAnalysis] Fetched keywords:', keywordTerms);
        
        if (keywordTerms.length > 0) {
          setDynamicKeywords(keywordTerms);
        } else {
          setDynamicKeywords(defaultKeywords);
        }
        
      } catch (error) {
        logger.error('[ProfileStrengthAnalysis] Error fetching dynamic keywords:', error);
        setDynamicKeywords(defaultKeywords);
      } finally {
        setKeywordsLoading(false);
      }
    };

    fetchDynamicKeywords();
  }, [businessData]);

  const handleKeywordToggle = (keyword) => {
    if (selectedKeywords.includes(keyword)) {
      setSelectedKeywords(selectedKeywords.filter(k => k !== keyword));
    } else if (selectedKeywords.length < 3) {
      setSelectedKeywords([...selectedKeywords, keyword]);
    }
    // After selecting a keyword, move focus to manual keywords input
    if (manualInputRef.current) {
      manualInputRef.current.focus();
    }
  };

  const handleOpenEdit = () => setEditDialogOpen(true);
  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setNewKeyword('');
  };
  const handleRemoveKeyword = (k) => setSelectedKeywords(selectedKeywords.filter(x => x !== k));
  const handleAddKeyword = () => {
    const k = newKeyword.trim();
    if (!k) return;
    if (selectedKeywords.length >= 3) return;
    if (selectedKeywords.includes(k)) return;
    setSelectedKeywords([...selectedKeywords, k]);
    setNewKeyword('');
  };

  const handleContinue = async () => {
    logger.log('[ProfileStrengthAnalysis] Continue button clicked');
    logger.log('[ProfileStrengthAnalysis] Selected account:', selectedAccount);
    logger.log('[ProfileStrengthAnalysis] Selected keywords:', selectedKeywords);
    logger.log('[ProfileStrengthAnalysis] Manual keywords:', manualKeywords);
    
    // Validation: Check if account is selected
    if (!selectedAccount) {
      alert('Please select an account first');
      return;
    }
    
    // Merge predefined selected + manual keywords (manual split by comma)
    const manualParts = manualKeywords
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);
    const keywords = [...selectedKeywords, ...manualParts].slice(0, 3);
    
    logger.log('[ProfileStrengthAnalysis] Keywords prepared:', keywords);
    
    // Resolve selected business for address and id
    const selectedBiz = accounts.find(a => a.name === selectedAccount) || null;
    const raw = selectedBiz?.raw || location.state?.selected || null;
    let address = selectedBiz?.address || raw?.address || raw?.formattedAddress || '';
    
    if (raw?.storefrontAddress) {
      const sa = raw.storefrontAddress;
      const parts = [];
      if (Array.isArray(sa.addressLines) && sa.addressLines.length) parts.push(sa.addressLines.join(', '));
      if (sa.locality) parts.push(sa.locality);
      if (sa.administrativeArea) parts.push(sa.administrativeArea);
      if (sa.postalCode) parts.push(sa.postalCode);
      if (sa.regionCode) parts.push(sa.regionCode);
      address = parts.join(', ');
    }

    logger.log('[ProfileStrengthAnalysis] Navigating immediately to results page...');
    
    // Navigate IMMEDIATELY to show loading popup - all competitor discovery will happen on results page
    navigate('/profile-strength-results', {
      state: {
        business: selectedBiz,
        keywords,
        address,
        selected: raw,
        startAnalysis: true // Flag to trigger analysis and competitor discovery on results page
      }
    });
  };

  return (
    <DashboardLayout>
      <MainContent>
        <ContentSection>
          <HeaderSection>
            <MainTitle>Just one step</MainTitle>
            <SubTitle>Let's analyses the strength of your profile</SubTitle>
          </HeaderSection>
          
          <Description>
          Running your first visibility diagnostic… We’ll review your business profile’s strengths and uncover what’s holding it back
          </Description>

          <FormSection>
            <FormContainer>
              <FormFieldContainer>
                <Box>
                  <FieldLabel>Select Account*</FieldLabel>
                  <Box sx={{ position: 'relative' }}>
                    <StyledSelect ref={accountSelectRef} onClick={() => setAccountMenuOpen((v) => !v)}>
                      <SelectPlaceholder>{selectedAccount || 'Enter Account'}</SelectPlaceholder>
                      <DropdownArrowIcon width={7} height={4} color="#0B91D6" />
                    </StyledSelect>
                    {accountMenuOpen && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: '52px',
                          backgroundColor: '#fff',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          zIndex: 10,
                          maxHeight: '220px',
                          overflowY: 'auto'
                        }}
                      >
                        {accounts.map((acc) => (
                          <Box
                            key={acc.name}
                            sx={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: '#F9FAFB' }
                            }}
                            onClick={() => {
                              setSelectedAccount(acc.name);
                              setAccountMenuOpen(false);
                            }}
                          >
                            {acc.name}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box>
                  <FieldLabel>Select Your keywords*(Select up-to three)</FieldLabel>
                  <StyledSelect ref={keywordsSelectRef} onClick={() => manualInputRef.current && manualInputRef.current.focus()}>
                    {selectedKeywords.length ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {selectedKeywords.map((k) => (
                          <Chip
                            key={k}
                            label={k}
                            size="small"
                            onDelete={() => handleKeywordToggle(k)}
                            sx={{
                              borderRadius: '16px',
                              backgroundColor: '#0B91D6',
                              color: '#fff',
                              '& .MuiChip-deleteIcon': { color: '#fff' }
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <SelectPlaceholder>Select keywords</SelectPlaceholder>
                    )}
                    <DropdownArrowIcon width={7} height={4} color="#A0A0AA" />
                  </StyledSelect>
                  
                  <KeywordsContainer>
                    {keywordsLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="textSecondary">
                          Loading business-specific keywords...
                        </Typography>
                      </Box>
                    ) : (
                      dynamicKeywords.map((keyword) => (
                        <KeywordChip
                          key={keyword}
                          label={keyword}
                          selected={selectedKeywords.includes(keyword)}
                          onClick={() => handleKeywordToggle(keyword)}
                          clickable
                        />
                      ))
                    )}
                  </KeywordsContainer>
                </Box>
              </FormFieldContainer>

              <Box>
                <FieldLabel>Write Keywords Manually</FieldLabel>
                <ManualKeywordsField
                  fullWidth
                  placeholder="Enter keywords"
                  value={manualKeywords}
                  onChange={(e) => setManualKeywords(e.target.value)}
                  variant="outlined"
                  inputRef={manualInputRef}
                />
              </Box>
            </FormContainer>

            <ContinueButton onClick={handleContinue}>
              Continue
            </ContinueButton>
          
          </FormSection>
        </ContentSection>
      </MainContent>

      {/* Analysis popup removed; results page will handle showing the unified loading popup */}
    </DashboardLayout>
  );
};

export default ProfileStrengthAnalysis;